import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';

const NAVY = '#1E3A6E';
const GOLD = '#F5C518';

const METRICS = [
  { key:'total_pts',  label:'Points',    icon:'⭐', color:'#F5C518', cmpColor:'#1E3A6E', lite:'#fffbeb' },
  { key:'runs',       label:'Runs',      icon:'🏏', color:'#0891b2', cmpColor:'#f59e0b', lite:'#ecfeff' },
  { key:'wickets',    label:'Wickets',   icon:'🎯', color:'#1E3A6E', cmpColor:'#F5C518', lite:'#e8edf5' },
  { key:'catches',    label:'Catches',   icon:'🙌', color:'#059669', cmpColor:'#f59e0b', lite:'#ecfdf5' },
  { key:'fours',      label:'Fours',     icon:'4️⃣', color:'#f59e0b', cmpColor:'#1E3A6E', lite:'#fffbeb' },
  { key:'sixes',      label:'Sixes',     icon:'6️⃣', color:'#0f766e', cmpColor:'#f59e0b', lite:'#f0fdfa' },
  { key:'bat_sr',     label:'Bat SR',    icon:'⚡', color:'#0891b2', cmpColor:'#F5C518', lite:'#ecfeff', isRate:true },
  { key:'bat_avg',    label:'Bat Avg',   icon:'📈', color:'#1E3A6E', cmpColor:'#F5C518', lite:'#e8edf5', isRate:true },
  { key:'bowl_econ',  label:'Economy',   icon:'🎳', color:'#0f766e', cmpColor:'#F5C518', lite:'#f0fdfa', isRate:true, lowerBetter:true },
  { key:'bowl_avg',   label:'Bowl Avg',  icon:'🏹', color:'#f59e0b', cmpColor:'#1E3A6E', lite:'#fffbeb', isRate:true, lowerBetter:true },
];

function processBreakdown(bd) {
  return (bd||[]).map(m => {
    const runs   = parseInt(m.runs)||0;
    const balls  = parseInt(m.balls)||0;
    const wickets= parseInt(m.wickets)||0;
    const overs  = parseFloat(m.overs)||0;
    const bwlRuns= parseInt(m.bowl_runs)||0;
    const bat_sr   = balls > 0 ? Math.round((runs/balls)*1000)/10 : 0;
    const bat_avg  = balls > 0 ? Math.round((runs)*10)/10 : 0; // runs scored (avg per knock shown in totals)
    const bowl_econ= overs > 0 ? Math.round((bwlRuns/overs)*100)/100 : 0;
    const bowl_avg = wickets > 0 ? Math.round((bwlRuns/wickets)*10)/10 : 0;
    return {
      label: new Date(m.match_date).toLocaleDateString('en-IN',{day:'numeric',month:'short'}),
      labelFull: new Date(m.match_date).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'}),
      match_date: m.match_date,
      total_pts: Math.round((parseFloat(m.total_pts)||0)*10)/10,
      runs, balls, wickets, overs, bowl_runs: bwlRuns,
      catches: parseInt(m.catches)||0,
      fours: parseInt(m.fours)||0,
      sixes: parseInt(m.sixes)||0,
      bat_sr, bat_avg, bowl_econ, bowl_avg,
    };
  });
}

// Tooltip shown on tap
function BarChart({ myData, cmpData, cmpName, myName, metric }) {
  const [tip, setTip] = useState(null);
  const m = METRICS.find(x => x.key === metric);
  const allV = [...myData.map(d=>d[metric]), ...(cmpData||[]).map(d=>d[metric])];
  const maxVal = Math.max(...allV, 1);
  const H = 140;
  const DATE_H = 36; // height reserved for stacked date labels
  const Y_AXIS_W = 28; // width for y-axis numbers
  const CMP = !!cmpData;

  // Compute 4 nice y-axis tick values
  const yTicks = [0.25, 0.5, 0.75, 1].map(f => {
    const raw = f * maxVal;
    // Round to a nice number
    if (maxVal <= 10) return Math.round(raw);
    if (maxVal <= 100) return Math.round(raw / 5) * 5;
    return Math.round(raw / 10) * 10;
  });

  // Parse label "25 Mar" → day + month parts
  function splitLabel(label) {
    const parts = label.split(' ');
    return { day: parts[0], mon: (parts[1]||'').toUpperCase() };
  }

  return (
    <div style={{width:'100%',position:'relative'}}>
      {/* Outer layout: y-axis + chart area */}
      <div style={{display:'flex', width:'100%', position:'relative', height: H + DATE_H}}>

        {/* Y-axis numbers */}
        <div style={{
          width: Y_AXIS_W, flexShrink:0, position:'relative', height: H + DATE_H,
        }}>
          {yTicks.map((val, fi) => {
            const f = [0.25,0.5,0.75,1][fi];
            return (
              <div key={fi} style={{
                position:'absolute',
                bottom: DATE_H + Math.round(f * H) - 7,
                right: 4,
                fontSize:'0.55rem',
                fontFamily:'Rajdhani,sans-serif',
                fontWeight:700,
                color:'#94a3b8',
                lineHeight:1,
              }}>{val}</div>
            );
          })}
        </div>

        {/* Chart area */}
        <div style={{flex:1, position:'relative', height: H + DATE_H}}>
          {/* Dashed guide lines */}
          {[0.25,0.5,0.75,1].map(f => (
            <div key={f} style={{
              position:'absolute', left:0, right:0,
              bottom: DATE_H + Math.round(f*H),
              borderTop:'1px dashed #e8ecf0',
            }} />
          ))}

          {/* Bars + date labels */}
          <div style={{
            position:'absolute', bottom: DATE_H, left:0, right:0, top:0,
            display:'flex', alignItems:'flex-end', gap:2,
          }}>
            {myData.map((d, i) => {
              const val  = d[metric];
              const cval = cmpData?.[i]?.[metric] ?? null;
              const h    = val  > 0 ? Math.max(Math.round((val /maxVal)*H), 5) : 2;
              const ch   = cval > 0 ? Math.max(Math.round((cval/maxVal)*H), 5) : 2;
              const { day, mon } = splitLabel(d.label);

              return (
                <div key={i}
                  onClick={() => setTip(tip === i ? null : i)}
                  style={{flex:1, display:'flex', flexDirection:'column', alignItems:'center', cursor:'pointer', height:'100%', justifyContent:'flex-end'}}
                >
                  <div style={{display:'flex', alignItems:'flex-end', gap:1, width:'100%', justifyContent:'center'}}>
                    <div style={{
                      flex:1, maxWidth: CMP ? 14 : 24,
                      height: h,
                      background: val>0 ? m.color : '#e8ecf0',
                      borderRadius:'3px 3px 0 0',
                      opacity: tip !== null && tip !== i ? 0.4 : 1,
                      transition:'all 0.3s ease',
                    }} />
                    {CMP && (
                      <div style={{
                        flex:1, maxWidth:14,
                        height: ch,
                        background: cval>0 ? m.cmpColor : '#e8ecf0',
                        borderRadius:'3px 3px 0 0',
                        opacity: tip !== null && tip !== i ? 0.4 : 1,
                        transition:'all 0.3s ease',
                      }} />
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Date labels row — below bars */}
          <div style={{
            position:'absolute', bottom:0, left:0, right:0,
            height: DATE_H,
            display:'flex', alignItems:'flex-start',
          }}>
            {myData.map((d, i) => {
              const { day, mon } = splitLabel(d.label);
              return (
                <div key={i} style={{
                  flex:1, display:'flex', flexDirection:'column', alignItems:'center',
                  paddingTop:3,
                }}>
                  <span style={{
                    fontSize:'0.58rem', fontFamily:'Rajdhani,sans-serif', fontWeight:800,
                    color: NAVY, lineHeight:1.15, textAlign:'center',
                  }}>{day}</span>
                  <span style={{
                    fontSize:'0.52rem', fontFamily:'Rajdhani,sans-serif', fontWeight:600,
                    color:'#94a3b8', lineHeight:1.15, textAlign:'center', letterSpacing:'0.02em',
                  }}>{mon}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Legend */}
      {CMP && (
        <div style={{display:'flex', gap:16, marginTop:4, paddingLeft:2, alignItems:'center', flexWrap:'wrap'}}>
          <div style={{display:'flex', alignItems:'center', gap:5}}>
            <div style={{width:10, height:10, borderRadius:2, background: m.color}} />
            <span style={{fontSize:'0.73rem', fontWeight:700, color:NAVY, fontFamily:'Rajdhani,sans-serif'}}>{myName}</span>
          </div>
          <div style={{display:'flex', alignItems:'center', gap:5}}>
            <div style={{width:10, height:10, borderRadius:2, background:m.cmpColor}} />
            <span style={{fontSize:'0.73rem', color:'#64748b', fontFamily:'Rajdhani,sans-serif', fontWeight:600}}>{cmpName}</span>
          </div>
          <span style={{fontSize:'0.7rem', color: tip !== null ? NAVY : '#94a3b8', marginLeft:'auto', fontFamily:'Rajdhani,sans-serif', fontWeight: tip !== null ? 700 : 500, textAlign:'right'}}>
            {tip !== null
              ? <>{myData[tip]?.labelFull || myData[tip]?.label} — {myName}: <strong>{myData[tip]?.[metric] ?? 0}</strong>{cmpData?.[tip] && <> · {cmpName}: <strong>{cmpData[tip]?.[metric] ?? 0}</strong></>}</>
              : 'tap bar for details'}
          </span>
        </div>
      )}
      {!CMP && (
        <div style={{textAlign:'right', fontSize: tip !== null ? '0.72rem' : '0.65rem', color: tip !== null ? NAVY : '#94a3b8', marginTop:4, fontFamily:'Rajdhani,sans-serif', fontWeight: tip !== null ? 700 : 500}}>
          {tip !== null
            ? <>{myData[tip]?.labelFull || myData[tip]?.label} — {myName}: <strong>{myData[tip]?.[metric] ?? 0}</strong></>
            : 'tap bar for details'}
        </div>
      )}
    </div>
  );
}

export default function PerformancePage() {
  const { member } = useAuth();
  const [myData,     setMyData]     = useState([]);
  const [cmpData,    setCmpData]    = useState(null);  // aligned for chart
  const [cmpRaw,     setCmpRaw]     = useState(null);  // unaligned for stats
  const [members,    setMembers]    = useState([]);
  const [cmpId,      setCmpId]      = useState('');
  const [cmpName,    setCmpName]    = useState('');
  const [metric,     setMetric]     = useState('total_pts');
  const [loading,    setLoading]    = useState(true);
  const [cmpLoading, setCmpLoading] = useState(false);
  // "View player" mode — show someone else's stats as primary
  const [viewId,     setViewId]     = useState('');
  const [viewData,   setViewData]   = useState(null);
  const [viewName,   setViewName]   = useState('');
  const [viewLoading,setViewLoading]= useState(false);

  useEffect(() => {
    if (!member?.id) return;
    Promise.all([
      api.get(`/leaderboards/match-breakdown/${member.id}?season=2026`),
      api.get('/members'),
    ]).then(([bRes, mRes]) => {
      setMyData(processBreakdown(bRes.data.breakdown));
      setMembers((mRes.data.members||[]).filter(x=>!x.is_observer && x.id!==member.id));
    }).finally(() => setLoading(false));
  }, [member?.id]);

  async function loadComparison(id) {
    if (!id) { setCmpData(null); setCmpRaw(null); setCmpName(''); setCmpId(''); return; }
    setCmpId(id);
    const found = members.find(x=>x.id===id);
    setCmpName(found?.display_name?.split(' ')[0]||'Player');
    setCmpLoading(true);
    try {
      const res = await api.get(`/leaderboards/match-breakdown/${id}?season=2026`);
      const raw = processBreakdown(res.data.breakdown);
      // Align against the PRIMARY data (viewData if set, otherwise myData)
      const primaryData = viewData && viewData.length > 0 ? viewData : myData;
      const aligned = primaryData.map(my => {
        const match = raw.find(c=>c.match_date===my.match_date);
        return match||{...my,total_pts:0,runs:0,balls:0,wickets:0,overs:0,bowl_runs:0,catches:0,fours:0,sixes:0,bat_sr:0,bat_avg:0,bowl_econ:0,bowl_avg:0};
      });
      setCmpData(aligned);
      setCmpRaw(raw); // store unaligned for accurate stats
    } catch { setCmpData(null); setCmpRaw(null); }
    finally { setCmpLoading(false); }
  }

  async function loadView(id) {
    if (!id) { setViewData(null); setViewName(''); setViewId(''); setCmpData(null); setCmpId(''); return; }
    setViewId(id);
    const found = members.find(x=>x.id===id);
    setViewName(found?.display_name?.split(' ')[0]||'Player');
    setViewLoading(true);
    setCmpData(null); setCmpRaw(null); setCmpId(''); // clear comparison — user can re-add after
    try {
      const res = await api.get(`/leaderboards/match-breakdown/${id}?season=2026`);
      setViewData(processBreakdown(res.data.breakdown));
    } catch { setViewData(null); }
    finally { setViewLoading(false); }
  }

  const myName  = member?.display_name?.split(' ')[0]||'Me';
  const AM      = METRICS.find(x=>x.key===metric);
  const card    = {background:'white',borderRadius:16,boxShadow:'0 1px 4px rgba(0,0,0,0.06)',marginBottom:12};
  // If viewing another player, use their data as primary
  const activeData = viewData || myData;
  const activeName = viewData ? viewName : myName;
  const vals    = activeData.map(d=>d[metric]);
  const fmt     = v => v===0?'0':v%1===0?String(v):v.toFixed(1);
  // For rate metrics, Total = season rate, Best = best single match, Avg = season rate
  const isRate  = AM?.isRate;
  let total, maxV, avg;
  if (metric === 'bat_sr') {
    const tr = activeData.reduce((s,d)=>s+(d.runs||0),0);
    const tb = activeData.reduce((s,d)=>s+(d.balls||0),0);
    total = tb>0?Math.round(tr/tb*1000)/10:0; maxV=Math.max(...vals,0); avg=total;
  } else if (metric === 'bat_avg') {
    const tr = activeData.reduce((s,d)=>s+(d.runs||0),0);
    const tm = activeData.filter(d=>(d.balls||0)>0).length;
    total = tm>0?Math.round(tr/tm*10)/10:0; maxV=Math.max(...vals.map(v=>activeData.find(d=>d[metric]===v)?.runs||v),0); avg=total;
  } else if (metric === 'bowl_econ') {
    const tr = activeData.reduce((s,d)=>s+(d.bowl_runs||0),0);
    const to = activeData.reduce((s,d)=>s+(d.overs||0),0);
    total = to>0?Math.round(tr/to*100)/100:0; const econVals=vals.filter(v=>v>0); maxV=econVals.length?Math.min(...econVals):0; avg=total;
  } else if (metric === 'bowl_avg') {
    const tr = activeData.reduce((s,d)=>s+(d.bowl_runs||0),0);
    const tw = activeData.reduce((s,d)=>s+(d.wickets||0),0);
    total = tw>0?Math.round(tr/tw*10)/10:0; const bavgVals=vals.filter(v=>v>0); maxV=bavgVals.length?Math.min(...bavgVals):0; avg=total;
  } else {
    total  = vals.reduce((s,v)=>s+v,0);
    maxV   = Math.max(...vals,0);
    // For runs: avg = runs / innings where player actually batted (balls faced > 0)
    const batted = metric==='runs' ? activeData.filter(d=>(d.balls||0)>0).length : vals.filter(v=>v>0).length;
    const played = batted > 0 ? batted : vals.filter(v=>v>0).length;
    avg    = played>0?total/played:0;
  }
  // Use cmpRaw (unaligned, true data) for stats — cmpData is just for chart alignment
  const cmpSrc  = cmpRaw || cmpData;
  const cvals   = cmpData?cmpData.map(d=>d[metric]):[];
  let cTotal, cMaxV, cAvg;
  if (metric === 'bat_sr') {
    const cr=(cmpSrc||[]).reduce((s,d)=>s+(d.runs||0),0); const cb=(cmpSrc||[]).reduce((s,d)=>s+(d.balls||0),0);
    cTotal=cb>0?Math.round(cr/cb*1000)/10:0; const crVals=(cmpSrc||[]).map(d=>d.bat_sr).filter(v=>v>0); cMaxV=crVals.length?Math.max(...crVals):0; cAvg=cTotal;
  } else if (metric === 'bat_avg') {
    const cr=(cmpSrc||[]).reduce((s,d)=>s+(d.runs||0),0); const cm=(cmpSrc||[]).filter(d=>(d.balls||0)>0).length;
    cTotal=cm>0?Math.round(cr/cm*10)/10:0; const caVals=(cmpSrc||[]).map(d=>d.bat_avg).filter(v=>v>0); cMaxV=caVals.length?Math.max(...caVals):0; cAvg=cTotal;
  } else if (metric === 'bowl_econ') {
    const cr=(cmpSrc||[]).reduce((s,d)=>s+(d.bowl_runs||0),0); const co=(cmpSrc||[]).reduce((s,d)=>s+(d.overs||0),0);
    cTotal=co>0?Math.round(cr/co*100)/100:0; const ceVals=(cmpSrc||[]).map(d=>d.bowl_econ).filter(v=>v>0); cMaxV=ceVals.length?Math.min(...ceVals):0; cAvg=cTotal;
  } else if (metric === 'bowl_avg') {
    const cr=(cmpSrc||[]).reduce((s,d)=>s+(d.bowl_runs||0),0); const cw=(cmpSrc||[]).reduce((s,d)=>s+(d.wickets||0),0);
    cTotal=cw>0?Math.round(cr/cw*10)/10:0; const cbVals=(cmpSrc||[]).map(d=>d.bowl_avg).filter(v=>v>0); cMaxV=cbVals.length?Math.min(...cbVals):0; cAvg=cTotal;
  } else {
    cTotal=(cmpSrc||[]).reduce((s,d)=>s+(d[metric]||0),0);
    const cRawVals=(cmpSrc||[]).map(d=>d[metric]||0);
    cMaxV=cRawVals.length?Math.max(...cRawVals):0;
    const cBatted=metric==='runs'?(cmpSrc||[]).filter(d=>(d.balls||0)>0).length:(cmpSrc||[]).filter(d=>(d[metric]||0)>0).length;
    cAvg=cBatted>0?cTotal/cBatted:0;
  }

  if (loading) return (
    <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'60vh'}}>
      <div style={{width:32,height:32,border:`3px solid ${GOLD}`,borderTopColor:'transparent',borderRadius:'50%',animation:'spin 0.8s linear infinite'}} />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  return (
    <div style={{background:'#f8fafc',minHeight:'100vh'}}>

      {/* Hero */}
      <div style={{background:`linear-gradient(135deg,${NAVY} 0%,#162d5a 100%)`,padding:'12px 20px 14px',position:'relative',overflow:'hidden'}}>
        <div style={{position:'relative'}}>
          <div style={{fontFamily:'Rajdhani,sans-serif',fontWeight:700,fontSize:'0.62rem',letterSpacing:'0.14em',color:'rgba(245,197,24,0.6)',textTransform:'uppercase',marginBottom:1}}>Season 2026</div>
          <h1 style={{fontFamily:'Rajdhani,sans-serif',fontWeight:800,fontSize:'1.5rem',color:GOLD,margin:'0 0 2px',letterSpacing:'0.01em'}}>Performance</h1>
          <div style={{color:'rgba(255,255,255,0.4)',fontSize:'0.72rem'}}>{myData.length} matches · tap bar for details</div>
        </div>
      </div>

      <div style={{padding:'14px 14px 90px'}}>
        {activeData.length===0 ? (
          <div style={{...card,padding:32,textAlign:'center'}}>
            <div style={{fontSize:'2.5rem',marginBottom:10}}>📊</div>
            <div style={{fontFamily:'Rajdhani,sans-serif',fontWeight:700,color:NAVY,fontSize:'1.1rem'}}>No matches yet</div>
          </div>
        ) : (<>

          {/* Metric toggles — 5-col grid for clean alignment */}
          <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:6,marginBottom:14}}>
            {METRICS.map(m => {
              const active = metric===m.key;
              return (
                <button key={m.key} onClick={()=>setMetric(m.key)} style={{
                  padding:'6px 4px', borderRadius:10,
                  border: active?'none':'1.5px solid #e2e8f0',
                  background: active?m.color:'white',
                  color: active?'white':'#64748b',
                  fontFamily:'Rajdhani,sans-serif', fontWeight:700, fontSize:'0.75rem',
                  cursor:'pointer', transition:'all 0.18s',
                  boxShadow: active?`0 3px 10px ${m.color}40`:'0 1px 2px rgba(0,0,0,0.04)',
                  display:'flex', alignItems:'center', justifyContent:'center', gap:3,
                  letterSpacing:'0.01em', textAlign:'center',
                }}>
                  {m.icon} {m.label}
                </button>
              );
            })}
          </div>

          {/* Stats card */}
          <div style={{...card,padding:'14px 16px',marginBottom:12}}>
            {/* Column headers */}
            <div style={{display:'grid',gridTemplateColumns: isRate ? '1fr 90px 90px' : '1fr 80px 80px 80px',gap:4,marginBottom:10}}>
              <div style={{fontSize:'0.65rem',color:'#94a3b8',fontFamily:'Rajdhani,sans-serif',fontWeight:700,textTransform:'uppercase',letterSpacing:'0.08em'}}>Player</div>
              {(isRate ? ['Season', AM.lowerBetter ? 'Best (Low)' : 'Best Match'] : ['Total','Best','Avg']).map(l=>(
                <div key={l} style={{fontSize:'0.65rem',color:'#94a3b8',fontFamily:'Rajdhani,sans-serif',fontWeight:700,textTransform:'uppercase',letterSpacing:'0.08em',textAlign:'right'}}>{l}</div>
              ))}
            </div>
            {/* My stats */}
            <div style={{display:'grid',gridTemplateColumns: isRate ? '1fr 90px 90px' : '1fr 80px 80px 80px',gap:4,alignItems:'center',paddingBottom: cmpData?'10px':'0',borderBottom: cmpData?'1px solid #f1f5f9':'none'}}>
              <div style={{display:'flex',alignItems:'center',gap:7}}>
                <div style={{width:10,height:10,borderRadius:3,background:AM.color,flexShrink:0}} />
                <span style={{fontSize:'0.88rem',fontFamily:'Rajdhani,sans-serif',fontWeight:700,color:NAVY}}>{activeName}</span>
              </div>
              {(isRate ? [fmt(total),fmt(maxV)] : [fmt(total),fmt(maxV),fmt(Math.round(avg*10)/10)]).map((v,i)=>(
                <div key={i} style={{fontFamily:'Rajdhani,sans-serif',fontWeight:800,fontSize:'1.15rem',color:AM.color,textAlign:'right'}}>{v}</div>
              ))}
            </div>
            {/* Comparison stats */}
            {cmpData && (
              <div style={{display:'grid',gridTemplateColumns: isRate ? '1fr 90px 90px' : '1fr 80px 80px 80px',gap:4,alignItems:'center',marginTop:10}}>
                <div style={{display:'flex',alignItems:'center',gap:7}}>
                  <div style={{width:10,height:10,borderRadius:3,background:`${AM.color}55`,flexShrink:0,border:`1.5px solid ${AM.color}80`}} />
                  <span style={{fontSize:'0.88rem',fontFamily:'Rajdhani,sans-serif',fontWeight:700,color:'#64748b'}}>{cmpName}</span>
                </div>
                {(isRate ? [fmt(cTotal),fmt(cMaxV)] : [fmt(cTotal),fmt(cMaxV),fmt(Math.round(cAvg*10)/10)]).map((v,i)=>(
                  <div key={i} style={{fontFamily:'Rajdhani,sans-serif',fontWeight:800,fontSize:'1.15rem',color:'#94a3b8',textAlign:'right'}}>{v}</div>
                ))}
              </div>
            )}
          </div>

          {/* Chart card */}
          <div style={{...card,padding:'16px 14px 14px'}}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:16}}>
              <span style={{fontFamily:'Rajdhani,sans-serif',fontWeight:700,fontSize:'0.95rem',color:NAVY}}>
                {AM.icon} {AM.label} per Match
              </span>
              <span style={{fontFamily:'Rajdhani,sans-serif',fontWeight:700,fontSize:'0.72rem',color:'#1E3A6E',background:'#e8edf5',padding:'3px 10px',borderRadius:12}}>
                {activeData.length} MATCHES
              </span>
            </div>
            {(cmpLoading || viewLoading) ? (
              <div style={{height:180,display:'flex',alignItems:'center',justifyContent:'center'}}>
                <div style={{width:24,height:24,border:`2px solid ${AM.color}`,borderTopColor:'transparent',borderRadius:'50%',animation:'spin 0.7s linear infinite'}} />
              </div>
            ) : (
              <BarChart myData={activeData} cmpData={cmpData} metric={metric} myName={activeName} cmpName={cmpName} />
            )}
          </div>

          {/* View Player section */}
          <div style={{...card,padding:'14px 16px'}}>
            <div style={{fontFamily:'Rajdhani,sans-serif',fontWeight:700,fontSize:'0.9rem',color:NAVY,marginBottom:10,display:'flex',alignItems:'center',gap:7}}>
              👤 View Any Player
            </div>
            <select value={viewId} onChange={e=>loadView(e.target.value)} style={{
              width:'100%',padding:'10px 12px',border:'1.5px solid #e2e8f0',borderRadius:10,
              fontSize:'0.88rem',color:NAVY,background:'#f8fafc',outline:'none',cursor:'pointer',
              fontFamily:'DM Sans, sans-serif', appearance:'auto',
            }}>
              <option value=''>— Select a player to view stats —</option>
              {members.map(m=><option key={m.id} value={m.id}>{m.display_name} ({m.ucc_id})</option>)}
            </select>
            {viewData && (
              <button onClick={()=>loadView('')} style={{
                marginTop:8,padding:'6px 14px',borderRadius:8,border:'1.5px solid #fca5a5',
                background:'white',color:'#dc2626',fontSize:'0.78rem',fontWeight:600,cursor:'pointer',
                fontFamily:'Rajdhani,sans-serif',
              }}>✕ Back to My Stats</button>
            )}
          </div>

          {/* Compare section */}
          <div style={{...card,padding:'14px 16px'}}>
            <div style={{fontFamily:'Rajdhani,sans-serif',fontWeight:700,fontSize:'0.9rem',color:NAVY,marginBottom:10,display:'flex',alignItems:'center',gap:7}}>
              ⚔️ Compare with Player
            </div>
            <select value={cmpId} onChange={e=>loadComparison(e.target.value)} style={{
              width:'100%',padding:'10px 12px',border:'1.5px solid #e2e8f0',borderRadius:10,
              fontSize:'0.88rem',color:NAVY,background:'#f8fafc',outline:'none',cursor:'pointer',
              fontFamily:'DM Sans, sans-serif', appearance:'auto',
            }}>
              <option value=''>— Select a player to compare —</option>
              {members.map(m=><option key={m.id} value={m.id}>{m.display_name} ({m.ucc_id})</option>)}
            </select>
            {cmpData && (
              <button onClick={()=>loadComparison('')} style={{
                marginTop:8,padding:'6px 14px',borderRadius:8,border:'1.5px solid #fca5a5',
                background:'white',color:'#dc2626',fontSize:'0.78rem',fontWeight:600,cursor:'pointer',
                fontFamily:'Rajdhani,sans-serif',
              }}>✕ Remove Comparison</button>
            )}
          </div>

          {/* Season totals grid */}
          <div style={{...card,padding:'14px 16px'}}>
            <div style={{fontFamily:'Rajdhani,sans-serif',fontWeight:700,fontSize:'0.9rem',color:NAVY,marginBottom:12}}>
              Season Totals
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:8}}>
              {METRICS.map(m => {
                // For rate metrics, compute properly (not just sum)
                let t, ct;
                if (m.key === 'bat_sr') {
                  const tr = activeData.reduce((s,d)=>s+(d.runs||0),0);
                  const tb = activeData.reduce((s,d)=>s+(d.balls||0),0);
                  t = tb>0 ? Math.round(tr/tb*1000)/10 : 0;
                  const cr = cmpData?.reduce((s,d)=>s+(d.runs||0),0)||0;
                  const cb = cmpData?.reduce((s,d)=>s+(d.balls||0),0)||0;
                  ct = cmpData ? (cb>0 ? Math.round(cr/cb*1000)/10 : 0) : null;
                } else if (m.key === 'bat_avg') {
                  const tr = activeData.reduce((s,d)=>s+(d.runs||0),0);
                  const tm = activeData.filter(d=>(d.balls||0)>0).length;
                  t = tm>0 ? Math.round(tr/tm*10)/10 : 0;
                  const cr = cmpData?.reduce((s,d)=>s+(d.runs||0),0)||0;
                  const cm = cmpData?.filter(d=>(d.balls||0)>0).length||0;
                  ct = cmpData ? (cm>0 ? Math.round(cr/cm*10)/10 : 0) : null;
                } else if (m.key === 'bowl_econ') {
                  const tr = activeData.reduce((s,d)=>s+(d.bowl_runs||0),0);
                  const to = activeData.reduce((s,d)=>s+(d.overs||0),0);
                  t = to>0 ? Math.round(tr/to*100)/100 : 0;
                  const cr = cmpData?.reduce((s,d)=>s+(d.bowl_runs||0),0)||0;
                  const co = cmpData?.reduce((s,d)=>s+(d.overs||0),0)||0;
                  ct = cmpData ? (co>0 ? Math.round(cr/co*100)/100 : 0) : null;
                } else if (m.key === 'bowl_avg') {
                  const tr = activeData.reduce((s,d)=>s+(d.bowl_runs||0),0);
                  const tw = activeData.reduce((s,d)=>s+(d.wickets||0),0);
                  t = tw>0 ? Math.round(tr/tw*10)/10 : 0;
                  const cr = cmpData?.reduce((s,d)=>s+(d.bowl_runs||0),0)||0;
                  const cw = cmpData?.reduce((s,d)=>s+(d.wickets||0),0)||0;
                  ct = cmpData ? (cw>0 ? Math.round(cr/cw*10)/10 : 0) : null;
                } else {
                  t  = activeData.reduce((s,d)=>s+d[m.key],0);
                  ct = cmpData?cmpData.reduce((s,d)=>s+d[m.key],0):null;
                }
                const active = metric===m.key;
                return (
                  <div key={m.key} onClick={()=>setMetric(m.key)} style={{
                    borderRadius:12, padding:'12px 8px', textAlign:'center', cursor:'pointer',
                    background: active ? m.lite : '#f8fafc',
                    border: `1.5px solid ${active ? m.color+'44' : '#f1f5f9'}`,
                    transition:'all 0.15s',
                  }}>
                    <div style={{fontSize:'1rem',marginBottom:3}}>{m.icon}</div>
                    <div style={{fontFamily:'Rajdhani,sans-serif',fontWeight:800,fontSize:'1.2rem',color:m.color,lineHeight:1}}>{fmt(t)}</div>
                    {ct!==null && <div style={{fontSize:'0.62rem',color:'#94a3b8',marginTop:2,fontFamily:'Rajdhani,sans-serif'}}>vs {fmt(ct)}</div>}
                    <div style={{fontSize:'0.6rem',color:'#94a3b8',textTransform:'uppercase',letterSpacing:'0.06em',marginTop:3}}>{m.label}</div>
                  </div>
                );
              })}
            </div>
          </div>

        </>)}
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}
